/**
 * A revogação da certificação entregue (E170.F1.C04 — GUS-229 bloco 4.1, ADR-0123).
 *
 * Só para o certificador ou o responsável pela plataforma, e só com a versão assinada. O motivo é
 * obrigatório (mínimo do servidor); nada se apaga: o arquivo e o histórico ficam, o cliente perde
 * o acesso à versão revogada e o caso volta a aguardar a assinatura. O servidor é quem decide: a tela
 * só mostra o que ele recusar.
 */
import type { AxiosInstance } from 'axios';
import { useId, useState } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useCurrentRole } from '@/lib/auth/useCurrentRole';
import {
  MOTIVO_MINIMO_DA_REVOGACAO,
  RevogacaoError,
  revogarCertificacao,
} from '@/lib/conferencia_final';

const t = ptBR.conferenciaFinal.revogacao;

export interface RevogarCertificacaoProps {
  client: AxiosInstance;
  requisicaoId: string;
  /** A revogação gravou: a conferência relê. */
  onRevogada: () => void;
}

export function RevogarCertificacao({
  client,
  requisicaoId,
  onRevogada,
}: RevogarCertificacaoProps) {
  const { role, isPlatform } = useCurrentRole();
  const campo = useId();
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  if (!isPlatform || (role !== 'certificador' && role !== 'owner')) return null;
  const valido = motivo.trim().length >= MOTIVO_MINIMO_DA_REVOGACAO;

  async function revogar(): Promise<void> {
    setEnviando(true);
    setErro(null);
    try {
      await revogarCertificacao(client, requisicaoId, motivo.trim());
      onRevogada();
    } catch (err) {
      setErro(t.recusa[err instanceof RevogacaoError ? err.motivo : 'indisponivel']);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="flex flex-col gap-sc-2 border-t border-border pt-sc-3"
      data-testid="revogar-certificacao"
    >
      <h3 className="text-body-md font-semibold text-fg">{t.titulo}</h3>
      <p className="text-body-sm text-fg-muted">{t.explicacao}</p>
      <label htmlFor={campo} className="text-body-sm text-fg">
        {t.motivo}
      </label>
      <textarea
        id={campo}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        rows={3}
        maxLength={500}
        className="border border-border bg-surface p-sc-2 text-body-sm text-fg"
        data-testid="motivo-da-revogacao"
      />
      <p className="text-caption text-fg-muted">{t.ajuda}</p>
      <div>
        <Button
          variant="danger"
          disabled={!valido || enviando}
          onClick={() => void revogar()}
          data-testid="revogar"
        >
          {enviando ? t.revogando : t.revogar}
        </Button>
      </div>
      {erro !== null ? (
        <p role="alert" className="text-body-sm text-danger-700">
          {erro}
        </p>
      ) : null}
    </div>
  );
}
